import React, { useCallback, useMemo, useState } from "react"

import { Button, Divider, toast } from "@coval/ui"
import { Clock, X } from "lucide-react"

import {
  DateRangeFilterButton,
  type DateRangeBounds,
} from "@/components/date-range/DateRangeFilterButton"
import {
  DynamicDateRangeInput,
  type DynamicDateRange,
} from "@/components/date-range/DynamicDateRangeInput"
import type { DatePreset } from "@/types/DatePreset"
import { validateCustomDateRange } from "@/utils/date/dateRange"
import type { DateRangePreset } from "@/utils/date/dateRangeTypes"

import { useGlobalDateRangeOverride } from "../hooks/useGlobalDateRangeOverride"
import type { DashboardDatePreferences } from "../types/dashboardConfig"
import { getAllPresetConfigs } from "../utils/dateRangePresets"

/**
 * Saving and clearing the dashboard-level default range. Omit to render the
 * range picker on its own, for surfaces with no dashboard default to manage
 * (e.g. the widget builder preview).
 */
interface DatePreferencesController {
  savedPreferences?: DashboardDatePreferences
  saveDatePreference: (preferences: DashboardDatePreferences) => Promise<void>
  clearDatePreference: () => Promise<void>
  isSaving: boolean
}

interface DateRangeSelectorProps {
  datePreferences?: DatePreferencesController
}

/**
 * Quick Select carries the preset key so a range saved as this dashboard's
 * default stays relative — a saved "Last 7 days" recalculates on every load
 * rather than freezing the week it was picked in.
 */
interface DashboardDatePreset extends DatePreset {
  key: DateRangePreset
}

const QUICK_SELECT_PRESETS: DashboardDatePreset[] = getAllPresetConfigs()
  .filter((config) => !config.isCustom)
  .map((config) => ({
    key: config.key,
    label: config.label,
    getValue: config.calculateRange,
  }))

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  datePreferences,
}) => {
  const {
    setGlobalOverride,
    setCustomGlobalOverride,
    setDynamicDateRangeOverride,
    currentOverride,
  } = useGlobalDateRangeOverride()

  const savedPreferences = datePreferences?.savedPreferences
  const isSaving = datePreferences?.isSaving ?? false

  const [currentSelection, setCurrentSelection] = useState<{
    preset?: DateRangePreset
    customRange?: { start: Date; end: Date }
    dynamicRange?: DynamicDateRange
  } | null>(null)

  const [isDynamicModalOpen, setIsDynamicModalOpen] = useState(false)

  // Holds a custom range that cannot be applied yet, so a half-typed range
  // survives in the inputs instead of snapping back to the applied one.
  const [draftRange, setDraftRange] = useState<DateRangeBounds | null>(null)
  const [customRangeError, setCustomRangeError] = useState<string | null>(null)

  const appliedRange = useMemo<DateRangeBounds>(
    () => ({
      start: currentOverride?.start ?? null,
      end: currentOverride?.end ?? null,
    }),
    [currentOverride],
  )

  const hasUnsavedChanges = useMemo(() => {
    if (!currentSelection) {
      return false
    }

    if (currentSelection.preset) {
      return currentSelection.preset !== savedPreferences?.preset
    }

    if (currentSelection.customRange) {
      return !savedPreferences?.customRange
    }

    if (currentSelection.dynamicRange) {
      return !savedPreferences?.relativeConfig
    }

    return false
  }, [currentSelection, savedPreferences])

  const hasDefaultSet = Boolean(savedPreferences)

  const handlePresetSelect = useCallback(
    (preset: DashboardDatePreset) => {
      setGlobalOverride(preset.key)
      setCurrentSelection({ preset: preset.key })
      setDraftRange(null)
      setCustomRangeError(null)
    },
    [setGlobalOverride],
  )

  const handleRangeChange = useCallback(
    (range: DateRangeBounds) => {
      if (!range.start) {
        setDraftRange(range)
        setCustomRangeError("Set a start date")
        return
      }

      // A start with the end left empty runs until the current time — the
      // "from this date until now" window, for tracking how long it has been
      // since a chosen date.
      const isUntilNow = range.end === null
      const end = range.end ?? new Date()

      const validation = validateCustomDateRange(
        range.start,
        end,
        isUntilNow ? "from-date-until-now" : "fixed",
      )
      if (!validation.isValid) {
        setDraftRange(range)
        setCustomRangeError(validation.error ?? "Invalid date range")
        return
      }

      // Widgets read the override to the minute, so the times the inputs
      // collect are applied as typed rather than snapped to whole days.
      setCustomGlobalOverride({ start: range.start, end })
      setCurrentSelection({
        customRange: { start: range.start, end },
      })
      setDraftRange(null)
      setCustomRangeError(null)
    },
    [setCustomGlobalOverride],
  )

  const handleDynamicDateRangeSelect = useCallback(
    (dynamicRange: DynamicDateRange) => {
      const validationError = setDynamicDateRangeOverride(dynamicRange)
      if (validationError) {
        toast.warning({
          title: "Invalid date range",
          description: validationError,
        })
        return
      }
      setCurrentSelection({ dynamicRange })
      setDraftRange(null)
      setCustomRangeError(null)
    },
    [setDynamicDateRangeOverride],
  )

  const handleSaveAsDefault = useCallback(async () => {
    if (!currentSelection || !datePreferences) {
      return
    }

    try {
      if (currentSelection.preset) {
        await datePreferences.saveDatePreference({
          preset: currentSelection.preset,
        })
      } else if (currentSelection.customRange) {
        await datePreferences.saveDatePreference({
          customRange: {
            start: currentSelection.customRange.start.toISOString(),
            end: currentSelection.customRange.end.toISOString(),
          },
        })
      } else if (currentSelection.dynamicRange) {
        await datePreferences.saveDatePreference({
          relativeConfig: {
            amount: currentSelection.dynamicRange.value,
            unit: currentSelection.dynamicRange.unit,
          },
        })
      }
    } catch {
      // Silent fail
    }
  }, [currentSelection, datePreferences])

  const handleClearDefault = useCallback(async () => {
    if (!datePreferences) {
      return
    }

    try {
      await datePreferences.clearDatePreference()
    } catch {
      // Silent fail
    }
  }, [datePreferences])

  const renderExtraSections = useCallback(
    (closePopover: () => void) => (
      <>
        <Divider />
        <div className="p-4">
          <Button
            variant="ghost"
            className="w-full justify-start"
            disabled={isSaving}
            onClick={() => {
              closePopover()
              setIsDynamicModalOpen(true)
            }}
          >
            <Clock className="w-3 h-3" />
            Dynamic range...
          </Button>
        </div>

        {datePreferences && (
          <>
            <Divider />
            <div className="p-4">
              <div className="text-sm font-semibold mb-3">
                Dashboard Default
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  variant="ghost"
                  className="w-full justify-start"
                  disabled={!hasUnsavedChanges || isSaving}
                  onClick={() => void handleSaveAsDefault()}
                >
                  Set as default
                </Button>
                <Button
                  variant="ghost"
                  className="w-full justify-start"
                  disabled={!hasDefaultSet || isSaving}
                  onClick={() => void handleClearDefault()}
                >
                  <X className="w-3 h-3" />
                  Clear default
                </Button>
              </div>
            </div>
          </>
        )}
      </>
    ),
    [
      datePreferences,
      handleClearDefault,
      handleSaveAsDefault,
      hasDefaultSet,
      hasUnsavedChanges,
      isSaving,
    ],
  )

  return (
    <>
      <DateRangeFilterButton
        value={draftRange ?? appliedRange}
        presets={QUICK_SELECT_PRESETS}
        onPresetSelect={handlePresetSelect}
        onRangeChange={handleRangeChange}
        disabled={isSaving}
        customRangeError={customRangeError}
        renderExtraSections={renderExtraSections}
      />

      <DynamicDateRangeInput
        isOpen={isDynamicModalOpen}
        onClose={() => setIsDynamicModalOpen(false)}
        onDateRangeSelect={handleDynamicDateRangeSelect}
      />
    </>
  )
}
